<template>
  <b-card>
    <b-row class="d-flex align-items-center justify-content-between mb-2">
      <b-col cols="12" md="6" class="d-flex align-items-center">
        <b-button
          variant="outline"
          class="mr-2 text font-weight-bolder"
          :style="{ backgroundColor: getColorFromProfileName(activeProfile) }"
          style="color: white"
        >
          {{ activeProfile }}</b-button
        >
        <div>
          <h5 class="mb-0">Racing Bet Template Configuration</h5>
          <small class="text-muted">
            Scroll horizontally to view all betting configuration options
          </small>
        </div>
      </b-col>
      <b-col cols="12" md="6" class="d-flex align-items-end flex-column">
        <div class="mb-1">
          <b-button
            @click="handleCancelChange()"
            size="sm"
            variant="outline-secondary"
            class="mr-2"
          >
            Cancel
          </b-button>
          <b-button @click="handleSaveConfig" size="sm" variant="primary">
            Save Configuration
          </b-button>
        </div>
        <div>
          <a
            href="/template_config_racing .csv"
            download="template_config_racing .csv"
            class="download-button mr-1"
          >
            <feather-icon icon="ArrowDownIcon" />
            Sample CSV
          </a>
          <input
            ref="fileInput"
            type="file"
            accept=".csv"
            hidden
            @change="handleFileUpload"
          />
          <b-button size="sm" variant="outline-primary" @click="onClickInput">
            <feather-icon icon="UploadIcon" />

            Upload CSV
          </b-button>
        </div>
      </b-col>
    </b-row>
    <div :key="renderKey">
      <div class="table-wrapper">
        <table class="min-w-[8000px] text-sm text-left text-gray-900">
          <thead>
            <SpreadsheetHeader />
          </thead>
          <tbody>
            <SpreadsheetRow
              v-for="row in generateRows()"
              :key="row.key"
              :row="row"
              :config="row.data"
              @onCellChange="handleCellChange"
            />
          </tbody>
        </table>
      </div>
      <div class="mt-2">
        <td class="bet-type-col sticky-col">Bet Type</td>
        <td class="cell-col" style="background-color: #fef3c7">
          <div class="grid-9">
            <div>Cash Max Stake <br />(Fixed Avail)</div>
            <div>Bonus Max Stake <br />(Fixed Avail)</div>
            <div>Cash Max Liability <br />(Fixed Avail)</div>
            <div>Bonus Max Liability <br />(Fixed Avail)</div>
            <div>Cash Max Stake <br />(No Fixed)</div>
            <div>Bonus Max Stake <br />(No Fixed)</div>
            <div>Cash Max Liability <br />(No Fixed)</div>
            <div>Bonus Max Liability <br />(No Fixed)</div>
            <div>Accept</div>
          </div>
        </td>
      </div>
      <div>
        <td class="bet-type-col sticky-col">MULTI</td>
        <td class="cell-col">
          <div class="grid-9">
            <EditCell
              v-for="field in [
                'cashMaxStake',
                'bonusMaxStake',
                'maxCashLia',
                'maxBonusLia',
                'cashMaxStakeNoFixedOdds',
                'bonusMaxStakeNoFixedOdds',
                'maxCashLiaNoFixedOdds',
                'maxBonusLiaNoFixedOdds',
                'accept',
              ]"
              :key="field"
              :value="getVal(field)"
              :type="field === 'accept' ? 'select' : 'input'"
              :options="field === 'accept' ? ['Auto', 'Manual'] : []"
              placeholder="0"
              @changeInput="(val) => emitCellChange(field, val)"
            />
          </div>
        </td>
      </div>
    </div>
    <div class="d-flex justify-content-end align-items-center mt-1">
      <b-button
        @click="handleCancelChange()"
        size="sm"
        variant="outline-secondary"
        class="mr-2"
      >
        Cancel
      </b-button>
      <!-- <div> -->
      <b-button @click="handleSaveConfig" size="sm" variant="primary">
        Save Configuration
      </b-button>
      <!-- </div> -->
    </div>
  </b-card>
</template>

<script>
import SpreadsheetHeader from "./SpreadsheetHeader.vue";
import SpreadsheetRow from "./SpreadsheetRow.vue";
import { BBadge, BButton, BCard, BCol, BRow } from "bootstrap-vue";
import { mapActions, mapState } from "vuex";
import { parse } from "csv-parse";
import useToastHandler from "@/services/toastHandler";
import EditCell from "./EditCell.vue";

// ----------------------------------------------------------------------
const INITIAL_RACING_BET_TYPES = [
  { betType: "WIN", key: "win" },
  { betType: "PLACE", key: "place" },
  { betType: "SP", key: "sp" },
  { betType: "BEST DIV SP", key: "best-div-sp" },
  { betType: "MID DIV WIN", key: "mid-div-win" },
  { betType: "MID DIV PLACE", key: "mid-div-place" },
  { betType: "EXACTA", key: "exacta" },
  { betType: "QUINELLA", key: "quinella" },
  { betType: "TRIFECTA", key: "trifecta" },
  { betType: "FIRST FOUR", key: "first4" },
  { betType: "SAME RACE MULTI", key: "srm" },
  { betType: "EARLY QUADDIE", key: "earlyQuaddie" },
  { betType: "QUADDIE", key: "lateQuaddie" },
  { betType: "EACH WAY FIXED", key: "each-way-fixed" },
  { betType: "EACH WAY BT+ MID", key: "each-way-best-div-mid-place" },
  { betType: "EACH WAY MID MID", key: "each-way-mid-div-mid-div-place" },
  { betType: "TOP K", key: "top-k" },
  { betType: "DAILY DOUBLE", key: "daily-double" },
  { betType: "RUNNING DOUBLE", key: "running-double" },
  { betType: "TREBLE", key: "treble" },
];

const BASE_INNER_CONFIG = {
  cashMaxStake: "",
  bonusMaxStake: "",
  maxCashLia: "",
  maxBonusLia: "",
  cashMaxStakeNoFixedOdds: "",
  bonusMaxStakeNoFixedOdds: "",
  maxCashLiaNoFixedOdds: "",
  maxBonusLiaNoFixedOdds: "",
  accept: "Auto",
};

const createDefaultInnerConfig = () => ({
  metroThoroughbreds: { ...BASE_INNER_CONFIG },
  nonMetroThoroughbreds: { ...BASE_INNER_CONFIG },
  internationalThoroughbreds: { ...BASE_INNER_CONFIG },
  metroGreyhounds: { ...BASE_INNER_CONFIG },
  nonMetroGreyhounds: { ...BASE_INNER_CONFIG },
  internationalGreyhounds: { ...BASE_INNER_CONFIG },
  metroHarness: { ...BASE_INNER_CONFIG },
  nonMetroHarness: { ...BASE_INNER_CONFIG },
  internationalHarness: { ...BASE_INNER_CONFIG },
});
// ----------------------------------------------------------------------

export default {
  components: {
    SpreadsheetHeader,
    SpreadsheetRow,
    BBadge,
    BButton,
    BCard,
    EditCell,
    BRow,
    BCol,
  },
  data() {
    return {
      racingBetTypes: INITIAL_RACING_BET_TYPES,
      originalRacingBetTypes: [],
      renderKey: 0,
      multiItem: { ...BASE_INNER_CONFIG },
      originalMultiItem: { ...BASE_INNER_CONFIG },
    };
  },
  async created() {
    if (this.activeProfile) await this.fetchOriginalData();
  },
  computed: {
    ...mapState("bettingConfig/list", ["activeProfile", "loadingList"]),
  },
  watch: {
    activeProfile() {
      this.fetchOriginalData();
    },
  },
  methods: {
    ...mapActions({
      getConfigRacingList: "bettingConfig/list/getConfigRacingList",
      upsetProfileRaceConfig: "bettingConfig/list/upsetProfileRaceConfig",
    }),

    getMapping(meetingKey) {
      let meetingType = "";
      if (meetingKey.includes("Thoroughbreds")) meetingType = "R";
      else if (meetingKey.includes("Greyhounds")) meetingType = "G";
      else if (meetingKey.includes("Harness")) meetingType = "H";

      let jurisdiction = "";
      if (meetingKey.includes("international")) jurisdiction = "International";
      else if (meetingKey.includes("nonMetro")) jurisdiction = "NonMetro";
      else if (meetingKey.includes("metro")) jurisdiction = "Metro";

      return meetingType && jurisdiction ? { meetingType, jurisdiction } : null;
    },

    handleCellChange(betTypeLocation, section, field, value) {
      const row = this.racingBetTypes.find((i) => i.key === betTypeLocation);
      if (row && row.data[section]) {
        row.data[section][field] = value;
      }
    },

    generateRows() {
      return this.racingBetTypes.map(({ betType, key, data }) => ({
        betType,
        key,
        sectionKey: key,
        data,
      }));
    },
    lowercaseFirstLetter(str) {
      if (!str) {
        return str;
      }
      const firstLetter = str.charAt(0).toLowerCase();

      const restOfString = str.slice(1);

      return firstLetter + restOfString;
    },
    convertMeetingType(type, jurisdiction) {
      if (type === "R")
        return this.lowercaseFirstLetter(jurisdiction) + "Thoroughbreds";
      if (type === "G")
        return this.lowercaseFirstLetter(jurisdiction) + "Greyhounds";
      if (type === "H")
        return this.lowercaseFirstLetter(jurisdiction) + "Harness";
      return null;
    },

    async fetchOriginalData() {
      const res = await this.getConfigRacingList(this.activeProfile);

      let processedRacingBetTypes = INITIAL_RACING_BET_TYPES.map((i) => {
        return {
          ...i,
          data: createDefaultInnerConfig(),
        };
      });

      if (!res.length) {
        this.multiItem = { ...BASE_INNER_CONFIG };
      }

      res.forEach((item) => {
        const isCash = item.currencyMethod === "cash";
        const itemByType = processedRacingBetTypes.find(
          (i) => i.key === item.selectionType
        );
        if (itemByType) {
          const convertKey = this.convertMeetingType(
            item.meetingType,
            item.jurisdiction
          );
          if (convertKey && itemByType.data[convertKey]) {
            itemByType.data[convertKey] = {
              ...itemByType.data[convertKey],
              ...(isCash && { cashMaxStake: item.maxStake }),
              ...(!isCash && { bonusMaxStake: item.maxStake }),
              ...(isCash && { maxCashLia: item.maxLiability }),
              ...(!isCash && { maxBonusLia: item.maxLiability }),
              ...(isCash && {
                cashMaxStakeNoFixedOdds: item.maxStakeIfNoFixedOdds,
              }),
              ...(!isCash && {
                bonusMaxStakeNoFixedOdds: item.maxStakeIfNoFixedOdds,
              }),
              ...(isCash && {
                maxCashLiaNoFixedOdds: item.maxLiabilityIfNoFixedOdds,
              }),
              ...(!isCash && {
                maxBonusLiaNoFixedOdds: item.maxLiabilityIfNoFixedOdds,
              }),
              accept: item.autoAccept ? "Auto" : "Manual",
            };
          }
        }
        if (item.currencyMethod === "cash" && item.selectionType === "multi") {
          this.multiItem = {
            ...this.multiItem,
            cashMaxStake: item.maxStake,
            maxCashLia: item.maxLiability,
            cashMaxStakeNoFixedOdds: item.maxStakeIfNoFixedOdds,
            maxCashLiaNoFixedOdds: item.maxLiabilityIfNoFixedOdds,
            accept: item.autoAccept ? "Auto" : "Manual",
          };
        }
        if (item.currencyMethod === "bonus" && item.selectionType === "multi") {
          this.multiItem = {
            ...this.multiItem,
            bonusMaxStake: item.maxStake,
            maxBonusLia: item.maxLiability,
            bonusMaxStakeNoFixedOdds: item.maxStakeIfNoFixedOdds,
            maxBonusLiaNoFixedOdds: item.maxLiabilityIfNoFixedOdds,
            accept: item.autoAccept ? "Auto" : "Manual",
          };
        }
      });
      this.racingBetTypes = processedRacingBetTypes;
      this.originalRacingBetTypes = JSON.parse(
        JSON.stringify(processedRacingBetTypes)
      );
      this.originalMultiItem = JSON.parse(JSON.stringify(this.multiItem));
    },

    handleCancelChange() {
      if (this.originalRacingBetTypes.length) {
        this.racingBetTypes = JSON.parse(
          JSON.stringify(this.originalRacingBetTypes)
        );
        this.multiItem = JSON.parse(JSON.stringify(this.originalMultiItem));
        this.renderKey += 1;
      } else {
        this.fetchOriginalData();
      }
    },

    async handleSaveConfig() {
      const list = [];

      this.racingBetTypes.forEach((betType) => {
        Object.keys(betType.data).forEach((meetingKey) => {
          const meetingData = betType.data[meetingKey];
          const mapping = this.getMapping(meetingKey);

          if (mapping) {
            const baseEntry = {
              meetingType: mapping.meetingType,
              jurisdiction: mapping.jurisdiction,
              selectionType: betType.key,
              autoAccept: meetingData.accept === "Auto",
            };

            // Cash entry
            list.push({
              ...baseEntry,
              currencyMethod: "cash",
              maxStake: Number(meetingData.cashMaxStake) || 0,
              maxLiability: Number(meetingData.maxCashLia) || 0,
              maxStakeIfNoFixedOdds:
                Number(meetingData.cashMaxStakeNoFixedOdds) || 0,
              maxLiabilityIfNoFixedOdds:
                Number(meetingData.maxCashLiaNoFixedOdds) || 0,
            });

            // Bonus entry
            list.push({
              ...baseEntry,
              currencyMethod: "bonus",
              maxStake: Number(meetingData.bonusMaxStake) || 0,
              maxLiability: Number(meetingData.maxBonusLia) || 0,
              maxStakeIfNoFixedOdds:
                Number(meetingData.bonusMaxStakeNoFixedOdds) || 0,
              maxLiabilityIfNoFixedOdds:
                Number(meetingData.maxBonusLiaNoFixedOdds) || 0,
            });
          }
        });
      });

      const res = await this.upsetProfileRaceConfig({
        profileTypeId: this.activeProfile,
        configs: [
          ...list,
          {
            selectionType: "multi",
            currencyMethod: "cash",
            maxStake: Number(this.multiItem.cashMaxStake) || 0,
            maxLiability: Number(this.multiItem.maxCashLia) || 0,
            maxStakeIfNoFixedOdds:
              Number(this.multiItem.cashMaxStakeNoFixedOdds) || 0,
            maxLiabilityIfNoFixedOdds:
              Number(this.multiItem.maxCashLiaNoFixedOdds) || 0,
            autoAccept: this.multiItem.accept === "Auto",
          },
          {
            selectionType: "multi",
            currencyMethod: "bonus",
            maxStake: Number(this.multiItem.bonusMaxStake) || 0,
            maxLiability: Number(this.multiItem.maxBonusLia) || 0,
            maxStakeIfNoFixedOdds:
              Number(this.multiItem.bonusMaxStakeNoFixedOdds) || 0,
            maxLiabilityIfNoFixedOdds:
              Number(this.multiItem.maxBonusLiaNoFixedOdds) || 0,
            autoAccept: this.multiItem.accept === "Auto",
          },
        ],
      });
      if (res.data) {
        this.showToastSuccess(true, "Racing configuration saved successfully");
      } else {
        this.showToastError(false, "Failed to save racing configuration.");
        this.handleCancelChange();
      }
    },
    onClickInput() {
      const fileInput = this.$refs.fileInput;
      fileInput.click();
    },
    _resetInputFile() {
      this.$refs.fileInput.value = null;
    },
    handleFileUpload(event) {
      const file = event.target.files[0];
      const reader = new FileReader();
      reader.onload = () => {
        this.parseCSV(reader.result);
      };
      reader.readAsText(file);
      this._resetInputFile();
    },
    parseCSV(csvData) {
      parse(csvData, { delimiter: "," }, (err, data) => {
        if (err) {
          console.error(err);
          return;
        }
        data.shift();
        this.racingBetTypes = INITIAL_RACING_BET_TYPES.map((i, index) => {
          const rowData = data[index + 1];
          const updatedInnerConfig = {
            metroThoroughbreds: {
              cashMaxStake: rowData[1],
              bonusMaxStake: rowData[2],
              maxCashLia: rowData[3],
              maxBonusLia: rowData[4],
              cashMaxStakeNoFixedOdds: rowData[5],
              bonusMaxStakeNoFixedOdds: rowData[6],
              maxCashLiaNoFixedOdds: rowData[7],
              maxBonusLiaNoFixedOdds: rowData[8],
              accept: rowData[9],
            },
            nonMetroThoroughbreds: {
              cashMaxStake: rowData[10],
              bonusMaxStake: rowData[11],
              maxCashLia: rowData[12],
              maxBonusLia: rowData[13],
               cashMaxStakeNoFixedOdds: rowData[14],
              bonusMaxStakeNoFixedOdds: rowData[15],
              maxCashLiaNoFixedOdds: rowData[16],
              maxBonusLiaNoFixedOdds: rowData[17],
              accept: rowData[18],
            },
            internationalThoroughbreds: {
              cashMaxStake: rowData[19],
              bonusMaxStake: rowData[20],
              maxCashLia: rowData[21],
              maxBonusLia: rowData[22],
               cashMaxStakeNoFixedOdds: rowData[23],
              bonusMaxStakeNoFixedOdds: rowData[24],
              maxCashLiaNoFixedOdds: rowData[25],
              maxBonusLiaNoFixedOdds: rowData[26],
              accept: rowData[27],
            },
            metroGreyhounds: {
              cashMaxStake: rowData[28],
              bonusMaxStake: rowData[29],
              maxCashLia: rowData[30],
              maxBonusLia: rowData[31],
               cashMaxStakeNoFixedOdds: rowData[32],
              bonusMaxStakeNoFixedOdds: rowData[33],
              maxCashLiaNoFixedOdds: rowData[34],
              maxBonusLiaNoFixedOdds: rowData[35],
              accept: rowData[36],
            },
            nonMetroGreyhounds: {
              cashMaxStake: rowData[37],
              bonusMaxStake: rowData[38],
              maxCashLia: rowData[39],
              maxBonusLia: rowData[40],
               cashMaxStakeNoFixedOdds: rowData[41],
              bonusMaxStakeNoFixedOdds: rowData[42],
              maxCashLiaNoFixedOdds: rowData[43],
              maxBonusLiaNoFixedOdds: rowData[44],
              accept: rowData[45],
            },
            internationalGreyhounds: {
              cashMaxStake: rowData[46],
              bonusMaxStake: rowData[47],
              maxCashLia: rowData[48],
              maxBonusLia: rowData[49],
               cashMaxStakeNoFixedOdds: rowData[50],
              bonusMaxStakeNoFixedOdds: rowData[51],
              maxCashLiaNoFixedOdds: rowData[52],
              maxBonusLiaNoFixedOdds: rowData[53],
              accept: rowData[54],
            },
            metroHarness: {
              cashMaxStake: rowData[55],
              bonusMaxStake: rowData[56],
              maxCashLia: rowData[57],
              maxBonusLia: rowData[58],
               cashMaxStakeNoFixedOdds: rowData[59],
              bonusMaxStakeNoFixedOdds: rowData[60],
              maxCashLiaNoFixedOdds: rowData[61],
              maxBonusLiaNoFixedOdds: rowData[62],
              accept: rowData[63],
            },
            nonMetroHarness: {
              cashMaxStake: rowData[64],
              bonusMaxStake: rowData[65],
              maxCashLia: rowData[66],
              maxBonusLia: rowData[67],
               cashMaxStakeNoFixedOdds: rowData[68],
              bonusMaxStakeNoFixedOdds: rowData[69],
              maxCashLiaNoFixedOdds: rowData[70],
              maxBonusLiaNoFixedOdds: rowData[71],
              accept: rowData[72],
            },
            internationalHarness: {
              cashMaxStake: rowData[73],
              bonusMaxStake: rowData[74],
              maxCashLia: rowData[75],
              maxBonusLia: rowData[76],
               cashMaxStakeNoFixedOdds: rowData[77],
              bonusMaxStakeNoFixedOdds: rowData[78],
              maxCashLiaNoFixedOdds: rowData[79],
              maxBonusLiaNoFixedOdds: rowData[80],
              accept: rowData[81],
            },
          };

          return {
            ...i,
            data: updatedInnerConfig,
          };
        });
      });
    },
    getColorFromProfileName(name) {
      const colorList = [
        "#5A6A7A",
        "#F48FB1",
        "#8BC34A",
        "#F44336",
        "#FFEB3B",
        "#FF9800",
        "#03A9F4",
        "#4CAF50",
      ];
      if (!name || colorList.length === 0) {
        return "#CCCCCC";
      }
      let hash = 0;
      const cleanName = name.toLowerCase();

      for (let i = 0; i < cleanName.length; i++) {
        hash += cleanName.charCodeAt(i);
      }
      const colorIndex = hash % colorList.length;

      return colorList[colorIndex];
    },
    getVal(field) {
      return this.multiItem[field] !== undefined &&
        this.multiItem[field] !== null
        ? `${this.multiItem[field]}`
        : "";
    },
    emitCellChange(field, val) {
      this.multiItem[field] = val;
    },
  },
  setup() {
    const { showToastSuccess, showToastError } = useToastHandler();

    return {
      showToastSuccess,
      showToastError,
    };
  },
};
</script>

<style lang="scss" scoped>
.table-wrapper {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.custom-table {
  min-width: 8000px;
  font-size: 0.875rem;
  color: #111827;
  text-align: left;
  white-space: nowrap;
}

.bet-type-col {
  padding: 12px 16px;
  font-weight: 600;
  background: #f8fafc;
  font-size: 12px;
  position: sticky;
  left: 0;
  z-index: 10;
  min-width: 100px;
}

.cell-col {
  padding: 12px;
  width: 100%;
}

.grid-9 {
  display: grid;
  grid-template-columns: repeat(9, 1fr);
  gap: 16px;
}

.download-button {
  font-weight: 500;
  font-size: 13px;
  border: 1px solid #2b65a5;
  border-radius: 5px;
  padding: 6px 12px;
}
</style>